// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_BASE_URL from '../../config/api';
import { renderLatex } from './latexRenderer';

// Render a single line with inline bold (**text**) and LaTeX support
function renderInlineMarkdown(text) {
    var parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map(function(part, i) {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{renderLatex(part.slice(2, -2), i)}</strong>;
        }
        return renderLatex(part, i);
    });
}

function CurriculumPanel({ topic, token, onPrefillChat }) {
    const [curriculum, setCurriculum] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedParts, setSelectedParts] = useState([]);
    const [queryInput, setQueryInput] = useState('');
    const [queryLoading, setQueryLoading] = useState(false);
    const [queryResponse, setQueryResponse] = useState(null);
    const [showQueryBox, setShowQueryBox] = useState(false);
    const queryInputRef = useRef(null);
    const abortRef = useRef(null);

    // Fetch / generate curriculum when topic changes
    const fetchCurriculum = useCallback(() => {
        if (!topic || !token) return;
        setLoading(true);
        setError('');
        setCurriculum(null);
        setSelectedParts([]);
        setQueryResponse(null);
        setShowQueryBox(false);

        fetch(`${API_BASE_URL}/learnwithai/ai/curriculum?topic=${encodeURIComponent(topic)}&level=beginner`, {
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                if (data.success && data.data.curriculum) {
                    setCurriculum(data.data.curriculum);
                } else {
                    setError(data.message || 'Failed to load curriculum');
                }
            })
            .catch(() => setError('Network error loading curriculum'))
            .finally(() => setLoading(false));
    }, [topic, token]);

    useEffect(() => {
        fetchCurriculum();
    }, [fetchCurriculum]);

    useEffect(() => {
        if (showQueryBox && queryInputRef.current) {
            queryInputRef.current.focus();
        }
    }, [showQueryBox]);

    var togglePart = function(part) {
        setSelectedParts(function(prev) {
            var exists = prev.some(function(p) { return p.id === part.id; });
            if (exists) return prev.filter(function(p) { return p.id !== part.id; });
            return prev.concat([part]);
        });
        setShowQueryBox(true);
        setQueryResponse(null);
    };

    var clearSelection = function() {
        setSelectedParts([]);
        setShowQueryBox(false);
        setQueryResponse(null);
        setQueryInput('');
    };

    var sendQuery = function() {
        if (!queryInput.trim() || queryLoading || !curriculum) return;
        if (selectedParts.length === 0 && queryInput.trim()) {
            if (onPrefillChat) onPrefillChat(queryInput.trim());
            setQueryInput('');
            return;
        }

        if (abortRef.current) abortRef.current.abort();
        var controller = new AbortController();
        abortRef.current = controller;

        setQueryLoading(true);
        setQueryResponse(null);

        var curriculumId = curriculum._id;
        var parts = selectedParts.map(function(p) { return { partId: p.id, text: p.text }; });

        fetch(`${API_BASE_URL}/learnwithai/ai/curriculum-query`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                curriculumSessionId: curriculumId,
                selectedParts: parts,
                userQuestion: queryInput.trim(),
                topic,
            }),
            signal: controller.signal,
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                if (data.success) {
                    setQueryResponse(data.data);
                    if (data.data.sideNote && data.data.sideNotePosition) {
                        updateLocalSideNote(data.data.sideNote, data.data.sideNotePosition);
                        saveSideNote(curriculumId, data.data.sideNote, `after ${data.data.sideNotePosition}`);
                    }
                    setQueryInput('');
                } else {
                    setQueryResponse({ explanation: data.message || 'Failed to get explanation', sideNote: '', error: true });
                }
            })
            .catch(function(err) {
                if (err && err.name === 'AbortError') return;
                setQueryResponse({ explanation: 'Network error. Please try again.', sideNote: '', error: true });
            })
            .finally(function() { setQueryLoading(false); abortRef.current = null; });
    };

    var updateLocalSideNote = function(note, partId) {
        setCurriculum(function(prev) {
            if (!prev) return prev;
            var updateParts = function(parts) {
                return parts.map(function(p) {
                    if (p.id === partId) {
                        var notes = (p.sideNotes || []).slice();
                        if (!notes.includes(note)) notes.push(note);
                        return Object.assign({}, p, { sideNotes: notes });
                    }
                    return p;
                });
            };
            return Object.assign({}, prev, {
                modules: (prev.modules || []).map(function(mod) {
                    return Object.assign({}, mod, {
                        selectableParts: updateParts(mod.selectableParts || []),
                    });
                }),
                selectableParts: updateParts(prev.selectableParts || []),
            });
        });
    };

    var saveSideNote = function(curriculumId, note, position) {
        fetch(`${API_BASE_URL}/learnwithai/ai/update-curriculum`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ curriculumSessionId: curriculumId, sideNote: note, position }),
        }).catch(function() {});
    };

    var handleKeyDown = function(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendQuery();
        }
    };

    var regenerate = function() {
        if (!curriculum || !curriculum._id) { fetchCurriculum(); return; }
        setLoading(true);
        fetch(`${API_BASE_URL}/learnwithai/ai/curriculum/${curriculum._id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .catch(function() {})
            .finally(function() { fetchCurriculum(); });
    };

    // ── Render ──────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="lw-cur-panel">
                <div className="lw-cur-header">
                    <span className="lw-cur-header-title">Curriculum</span>
                </div>
                <div className="lw-cur-loading">
                    <div className="lw-cur-loading-dots">
                        <span></span><span></span><span></span>
                    </div>
                    <p>Generating curriculum for <strong>{topic}</strong>...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="lw-cur-panel">
                <div className="lw-cur-header">
                    <span className="lw-cur-header-title">Curriculum</span>
                </div>
                <div className="lw-cur-error">
                    <p>{error}</p>
                    <button className="lw-cur-retry-btn" onClick={fetchCurriculum}>Try Again</button>
                </div>
            </div>
        );
    }

    if (!curriculum) return null;

    return (
        <div className="lw-cur-panel">
            {/* Header */}
            <div className="lw-cur-header">
                <div className="lw-cur-header-info">
                    <span className="lw-cur-header-title">{curriculum.title || 'Curriculum'}</span>
                    {curriculum.fromCache && (
                        <span className="lw-cur-cache-badge">cached</span>
                    )}
                </div>
                <button className="lw-cur-regen-btn" onClick={regenerate} title="Regenerate curriculum">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                </button>
            </div>

            {/* Overview */}
            {curriculum.overview && (
                <div className="lw-cur-overview">
                    {curriculum.overview}
                </div>
            )}

            {/* Selection toolbar */}
            {selectedParts.length > 0 && (
                <div className="lw-cur-sel-bar">
                    <span className="lw-cur-sel-count">{selectedParts.length} selected</span>
                    <button className="lw-cur-sel-ask-btn" onClick={() => { setShowQueryBox(true); if (queryInputRef.current) queryInputRef.current.focus(); }}>
                        Ask AI
                    </button>
                    <button className="lw-cur-sel-clear" onClick={clearSelection}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Modules — rendered as simple markdown-like blocks */}
            <div className="lw-cur-modules">
                {(curriculum.modules || []).map(function(mod) {
                    return (
                        <div key={mod.id} className="lw-cur-module">
                            <h3 className="lw-cur-mod-heading">{mod.title}</h3>
                            <div className="lw-cur-mod-content">
                                {(mod.selectableParts || []).map(function(part) {
                                    var isSelected = selectedParts.some(function(p) { return p.id === part.id; });
                                    return (
                                        <div
                                            key={part.id}
                                            className={'lw-cur-line' + (isSelected ? ' lw-cur-line--selected' : '')}
                                            onClick={function() { togglePart(part); }}
                                            onDoubleClick={function() {
                                                setSelectedParts([part]);
                                                setShowQueryBox(true);
                                                setQueryInput('Explain this in detail');
                                            }}
                                            title="Click to select, double-click to ask AI"
                                        >
                                            <span className="lw-cur-line-text">
                                                {renderInlineMarkdown(part.text)}
                                            </span>
                                            {isSelected && <span className="lw-cur-line-check">&#10003;</span>}
                                            {part.sideNotes && part.sideNotes.length > 0 && (
                                                <div className="lw-cur-line-notes">
                                                    {part.sideNotes.map(function(note, i) {
                                                        return <span key={i} className="lw-cur-line-note">{note}</span>;
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Query box */}
            {(showQueryBox || selectedParts.length > 0) && (
                <div className="lw-cur-query-box">
                    {selectedParts.length > 0 && (
                        <div className="lw-cur-selected-preview">
                            {selectedParts.slice(0, 2).map(function(p) {
                                return (
                                    <span key={p.id} className="lw-cur-selected-chip">
                                        {p.text.slice(0, 40)}{p.text.length > 40 ? '...' : ''}
                                    </span>
                                );
                            })}
                            {selectedParts.length > 2 && (
                                <span className="lw-cur-selected-chip lw-cur-selected-chip--more">
                                    +{selectedParts.length - 2} more
                                </span>
                            )}
                        </div>
                    )}
                    <div className="lw-cur-query-input-row">
                        <input
                            ref={queryInputRef}
                            className="lw-cur-query-input"
                            value={queryInput}
                            onChange={function(e) { setQueryInput(e.target.value); }}
                            onKeyDown={handleKeyDown}
                            placeholder={selectedParts.length > 0 ? 'Ask about selected...' : 'Ask AI anything...'}
                            disabled={queryLoading}
                        />
                        <button
                            className="lw-cur-query-send"
                            onClick={sendQuery}
                            disabled={!queryInput.trim() || queryLoading}
                        >
                            {queryLoading ? '...' : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            )}
                        </button>
                    </div>

                    {queryLoading && (
                        <div className="lw-cur-query-loading">
                            <div className="lw-typing">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    )}

                    {queryResponse && (
                        <div className={`lw-cur-query-response ${queryResponse.error ? 'lw-cur-query-response--error' : ''}`}>
                            <div className="lw-cur-query-response-header">
                                <span>AI Clarification</span>
                                <button
                                    className="lw-cur-query-response-close"
                                    onClick={function() { setQueryResponse(null); }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                            <p className="lw-cur-query-explanation">{renderLatex(queryResponse.explanation)}</p>
                            {queryResponse.sideNote && (
                                <div className="lw-cur-query-sidenote">
                                    <em>Added to curriculum:</em> {queryResponse.sideNote}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Hint footer */}
            {selectedParts.length === 0 && !showQueryBox && (
                <div className="lw-cur-footer">
                    <span>Click any text to select it, then ask AI for clarification</span>
                </div>
            )}
        </div>
    );
}

export default CurriculumPanel;
